import { Button } from "../ui/button";
import { useTicketFollowers, useToggleTicketFollow } from "../../hooks/useTicketFollowers";
import { useAuth } from "../../hooks/useAuth";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";
import { Eye, EyeOff, Loader2, Users, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

interface TicketFollowButtonProps {
  ticketId: string;
  organizationId?: string;
  assignedToId?: string;
  variant?: "default" | "compact";
}

export function TicketFollowButton({ ticketId, organizationId, assignedToId, variant = "default" }: TicketFollowButtonProps) {
  const { user } = useAuth();
  const { data: followersData, isLoading } = useTicketFollowers(ticketId, organizationId);
  const toggleFollowMutation = useToggleTicketFollow();

  const isAssignedAgent = assignedToId && user?.id === assignedToId;
  const isFollowing = followersData?.isCurrentUserFollowing ?? false;
  const followerCount = followersData?.followerCount ?? 0;
  const followers = followersData?.followers ?? [];

  const handleToggleFollow = async () => {
    try {
      const result = await toggleFollowMutation.mutateAsync({ ticketId, organizationId });
      if (result.isFollowing) {
        showToast.success("Following", "You will receive notifications for this ticket");
      } else {
        showToast.success("Unfollowed", "You will no longer receive notifications for this ticket");
      }
    } catch (error) {
      console.error("Failed to toggle follow:", error);
      showToast.error("Error", getErrorMessage(error, "Failed to update follow status"));
    }
  };

  // If user is the assigned agent, only show the followers dropdown (no follow button)
  if (isAssignedAgent) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={variant === "compact" ? "sm" : "default"}
            className="gap-2"
            disabled={isLoading}
          >
            <Users className="h-4 w-4" />
            <span>{followerCount} {followerCount === 1 ? "Follower" : "Followers"}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Ticket Followers</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {followers.length === 0 ? (
            <div className="px-2 py-3 text-sm text-muted-foreground text-center">
              No one is following this ticket yet
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {followers.map((follower) => (
                <div
                  key={follower.id}
                  className="px-2 py-2 text-sm hover:bg-accent rounded-sm"
                >
                  <div className="font-medium">{follower.fullName}</div>
                  <div className="text-xs text-muted-foreground">
                    Following since {formatDistanceToNow(new Date(follower.followedAt), { addSuffix: true })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // For other agents/admins: show follow button with followers dropdown
  if (variant === "compact") {
    return (
      <div className="flex items-center gap-1">
        <Button
          variant={isFollowing ? "secondary" : "outline"}
          size="icon"
          onClick={handleToggleFollow}
          disabled={isLoading || toggleFollowMutation.isPending}
          className="h-8 w-8"
          title={isFollowing ? "Unfollow ticket" : "Follow ticket"}
        >
          {toggleFollowMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isFollowing ? (
            <Eye className="h-4 w-4" />
          ) : (
            <EyeOff className="h-4 w-4" />
          )}
        </Button>
        {followerCount > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 px-2 gap-1">
                <Users className="h-3 w-3" />
                <span className="text-xs">{followerCount}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Ticket Followers</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-64 overflow-y-auto">
                {followers.map((follower) => (
                  <div
                    key={follower.id}
                    className="px-2 py-2 text-sm hover:bg-accent rounded-sm"
                  >
                    <div className="font-medium">{follower.fullName}</div>
                    <div className="text-xs text-muted-foreground">
                      Following since {formatDistanceToNow(new Date(follower.followedAt), { addSuffix: true })}
                    </div>
                  </div>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="flex items-center gap-1">
      <Button
        variant={isFollowing ? "secondary" : "outline"}
        onClick={handleToggleFollow}
        disabled={isLoading || toggleFollowMutation.isPending}
        className="gap-2"
      >
        {toggleFollowMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isFollowing ? (
          <Eye className="h-4 w-4" />
        ) : (
          <EyeOff className="h-4 w-4" />
        )}
        <span>{isFollowing ? "Following" : "Follow"}</span>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 px-2">
            <Users className="h-4 w-4" />
            <span className="text-sm">{followerCount}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Ticket Followers</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {followers.length === 0 ? (
            <div className="px-2 py-3 text-sm text-muted-foreground text-center">
              No one is following this ticket yet
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              {followers.map((follower) => (
                <div
                  key={follower.id}
                  className="px-2 py-2 text-sm hover:bg-accent rounded-sm"
                >
                  <div className="font-medium">{follower.fullName}</div>
                  <div className="text-xs text-muted-foreground">
                    Following since {formatDistanceToNow(new Date(follower.followedAt), { addSuffix: true })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
